import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTable, useGlobalFilter, usePagination, useSortBy } from 'react-table';
import Header from '../../components/Header/Header';
import Table from '../../components/Table/Table';
import TableTabs from '../../components/TableTabs/TableTabs';
import Empty from '../../components/Empty/Empty';
import HookForm from '../../components/HookForm/HookForm';
import icon from '../../brand/generated/sidebar/deals.svg'; // reuse icon

import { DKP_SIGNINGS } from '../../constants/dkpSignColumns';
import { dkpSignFilterOptions } from '../../constants/dkpSignFilterOptions';
import getDkpSignings from '../../api/dkp/getDkpSignings';
import { setFilters } from '../../utils/setFilters';
import { useSelector } from 'react-redux';
import generateLink from '../../api/signature/generateLink';
import { DOCUMENT_TYPES_CONTRACT } from '../../constants';
import Modal from '../../components/Modal/Modal';
import PDF from '../../components/Pdf';
import Button from '../../components/Button/Button';
import alert from '../../helpers/alert';
import LoaderInline from '../../components/Loader/LoaderInline';

const DkpSign = () => {
  const user = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [signings, setSignings] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
    pageSize: 10,
  });
  const [isFetchAll, setFetchAll] = useState(false);

  const generateSignLink = (document, sendSms = false) => {
    setModalLoading(true);
    generateLink({
      url: document.originalUrl || document.url,
      name: document.name,
      typeId: DOCUMENT_TYPES_CONTRACT,
      documentId: document.id,
      userId: user.id,
      dealId: document.dealId,
      adminSignature: true,
      sendSms,
    })
      .then((response) => {
        if (!sendSms && response.data?.url) {
          return window.open(response.data, '_blank');
        }
        if (sendSms) {
          setIsOpen(false);
          setCurrentDocument(null);
          return alert.success('Ссылка на подписание отправлена на ваш телефон');
        }
      })
      .catch(() => {
        alert.error('Ошибка при генерации ссылки для подписания');
      })
      .finally(() => {
        setModalLoading(false);
      });
  };

  const [currentDocument, setCurrentDocument] = useState(null);
  const onSignDoc = useCallback((row) => {
    setCurrentDocument(row);
    setIsOpen(true);
  }, []);

  const columns = useMemo(() => DKP_SIGNINGS({ onSign: onSignDoc }), [onSignDoc]);

  const table = useTable(
    {
      columns,
      data: signings,
      initialState: { pageSize: pagination.pageSize },
      manualPagination: true,
      pageCount: pagination.totalPages,
      page: pagination.page - 1,
      disableSortRemove: true,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { page, setPageSize } = table;

  const [value, setValue] = useState('');
  const [queryParams, setQueryParams] = useState({});

  const fetchData = useCallback(
    (params) => {
      console.log('user', user);
      setLoading(true);
      getDkpSignings(params)
        .then((res) => {
          const data = res.data.items || [];
          const numbered = data.map((item, idx) => ({
            ...item,
            number: (params.page - 1) * params.pageSize + idx + 1,
          }));
          setSignings(numbered);
          setPagination({
            page: res.data?.page,
            totalPages: Math.ceil(res.data?.total / params.pageSize),
            pageSize: params.pageSize,
          });
          setDataCount(res.data.total);
        })
        .finally(() => setLoading(false));
    },
    [user]
  );

  useEffect(() => {
    if (!isFetchAll) {
      fetchData({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
    }
  }, [pagination.page, pagination.pageSize, queryParams, fetchData, isFetchAll]);

  const onFastSearch = useCallback((val) => {
    setQueryParams((prev) => ({ ...prev, all: val }));
  }, []);

  return (
    <>
      <Header title="Подписание ДКП" icon={icon} />
      <br />
      <br />
      <TableTabs
        setFilter={onFastSearch}
        value={value}
        filterComponent={
          <HookForm
            inputs={dkpSignFilterOptions}
            responsiveForm
            grid={{ display: 'grid', gap: '10px', gridTemplateColumns: '1fr 1fr 1fr 1fr' }}
            style={{ backgroundColor: 'transparent' }}
          />
        }
        handleSearchParams={(resetFilters) =>
          setFilters(resetFilters, setFetchAll, setPageSize, setPagination, setQueryParams, dkpSignFilterOptions)
        }
        setValue={setValue}
        inputPlaceholder={'Быстрый поиск'}
      />
      <br />
      {!page.length && !loading ? (
        <Empty />
      ) : (
        <Table
          table={table}
          all={'записей'}
          pagination
          sort
          setPagination={setPagination}
          setFetchAll={setFetchAll}
          isFetchAll={isFetchAll}
          setData={setSignings}
          p={pagination}
          loading={loading}
          setLoading={setLoading}
          columns={columns}
          setColumns={() => {}}
          columnsSettingsKey="dkp-signings"
          queryParams={queryParams}
          api={'/api/signature/dkp'}
          response={'signings'}
          isSortable
          dataCount={dataCount}
        />
      )}

      <Modal
        isOpen={isOpen && !!currentDocument}
        onRequestClose={() => setIsOpen(false)}
        contentStyle={{
          maxHeight: '100%',
          maxWidth: '100%',
        }}
        wrapperStyle={{
          display: 'flex',
          flexDirection: 'column',
          height: '95%',
          gap: 20,
          position: 'relative',
        }}
      >
        <>
          {modalLoading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              }}
            >
              <LoaderInline />
            </div>
          )}
          <div style={{ height: '100%' }}>
            <PDF url={currentDocument?.originalUrl || currentDocument?.url} style={{ height: 'calc(100vh - 250px)' }} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <Button fill disabled={modalLoading} onClick={() => generateSignLink(currentDocument, false)}>
              Подписать на этом устройстве
            </Button>
            <Button fill disabled={modalLoading} onClick={() => generateSignLink(currentDocument, true)}>
              Получить СМС со ссылкой на подписание
            </Button>
          </div>
        </>
      </Modal>
    </>
  );
};

export default DkpSign;
